import { Box, Card, Heading, Paragraph, Table, TextButton } from '@zillow/constellation-10';
import { useMainLayout } from '../../../layouts/MainPageLayout/MainLayoutContext';
import { isClaimedProperty } from '../../../../utils/isClaimedProperty';
import { getFilterKeyFromDisplayingRegionLevel } from '../../../SharedComponents/Map';
import { isNumber } from '../../../../utils/isNumber';
import { formatNumber } from '../../../../utils';
import { type PortfolioAnalyticsPropertyResponseDto } from '@zg-rentals/ts-customer-analytics-client';

const RegionStatsCard = ({ title, value }: { title: string; value: number | string }) => {
  return (
    <Card elevated={false} css={{ flexGrow: 1 }}>
      <Paragraph textStyle="body-sm-bold">{title}</Paragraph>
      <Heading level={5}>{isNumber(value) ? formatNumber(value) : value}</Heading>
    </Card>
  );
};

export const RegionStats = ({
  visibleProperties,
}: {
  visibleProperties: Array<PortfolioAnalyticsPropertyResponseDto>;
}) => {
  const {
    allProperties,
    regionsMapping,
    visibleRegionIds,
    onSelectState,
    onSelectMetropolitanServiceArea,
    onSelectCity,
    onSelectZIPCode,
  } = useMainLayout();
  const propertiesCount = allProperties.length;

  const sfProperties = visibleProperties.filter((property) => property.aggregateStats.isSingleFamily);
  const isAnyPropertySingleFamily = sfProperties.length > 0;

  const avgSfDaysOnMarket =
    sfProperties.reduce((acc, curr) => acc + curr.aggregateStats.daysOnMarketCount, 0) / sfProperties.length;

  const propertyRegions = visibleRegionIds.map(({ regionId, regionIdType }) => {
    const propertiesInRegion = allProperties.filter(
      (property) =>
        Number(property.propertyDetails.stateRegionId) === Number(regionId) ||
        Number(property.propertyDetails.metroStatRegionId) === Number(regionId) ||
        Number(property.propertyDetails.cityRegionId) === Number(regionId) ||
        Number(property.propertyDetails.zipRegionId) === Number(regionId),
    );

    const claimedPropertiesInRegion = allProperties.filter(
      (property) =>
        (Number(property.propertyDetails.stateRegionId) === Number(regionId) ||
          Number(property.propertyDetails.metroStatRegionId) === Number(regionId) ||
          Number(property.propertyDetails.cityRegionId) === Number(regionId) ||
          Number(property.propertyDetails.zipRegionId) === Number(regionId)) &&
        isClaimedProperty(property),
    );

    return {
      regionId,
      regionIdType,
      propertiesInRegion,
      claimedPropertiesInRegion,
      percentage: ((propertiesInRegion.length + claimedPropertiesInRegion.length) / (propertiesCount || 1)) * 100,
    };
  });

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: '400' }}>
      <Box css={{ display: 'flex', gap: '400' }}>
        <RegionStatsCard
          title="Active properties"
          value={propertyRegions.reduce((acc, curr) => acc + curr.propertiesInRegion.length, 0)}
        />
        <RegionStatsCard
          title="Claimed"
          value={propertyRegions.reduce((acc, curr) => acc + curr.claimedPropertiesInRegion.length, 0)}
        />
      </Box>
      <RegionStatsCard
        title="Active units"
        value={propertyRegions.reduce(
          (acc, curr) => acc + curr.propertiesInRegion.reduce((acc, curr) => acc + curr.propertyDetails.numUnits, 0),
          0,
        )}
      />
      {isAnyPropertySingleFamily && (
        <RegionStatsCard title="Average days on market (single family)" value={avgSfDaysOnMarket} />
      )}
      <Table.Responsive>
        <Table.Root aria-label="region-stats-table">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Advertising markets</Table.HeaderCell>
              <Table.HeaderCell>% of portfolio</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {propertyRegions.map(({ regionId, percentage, regionIdType, propertiesInRegion }) => {
              const filterKey = getFilterKeyFromDisplayingRegionLevel(regionIdType);
              const castRegionId = Number(regionId);
              return (
                <Table.Row key={regionId}>
                  <Table.Cell>
                    <TextButton
                      asChild
                      css={{
                        '& span': {
                          textDecoration: 'underline',
                          color: 'text.neutral !important',
                        },
                      }}
                      textStyle="body-sm"
                    >
                      <a
                        onClick={() => {
                          switch (filterKey) {
                            case 'stateRegionId':
                              onSelectState(castRegionId);
                              break;
                            case 'metropolitanServiceAreaRegionId':
                              if (propertiesInRegion.length > 0)
                                onSelectMetropolitanServiceArea(
                                  castRegionId,
                                  propertiesInRegion[0].propertyDetails.stateRegionId,
                                );
                              break;

                            case 'cityRegionId':
                              if (propertiesInRegion.length > 0)
                                onSelectCity(
                                  castRegionId,
                                  propertiesInRegion[0].propertyDetails.metroStatRegionId,
                                  propertiesInRegion[0].propertyDetails.stateRegionId,
                                );
                              break;
                            case 'zipCodeRegionId':
                              if (propertiesInRegion.length > 0)
                                onSelectZIPCode(
                                  castRegionId,
                                  propertiesInRegion[0].propertyDetails.cityRegionId,
                                  propertiesInRegion[0].propertyDetails.metroStatRegionId,
                                  propertiesInRegion[0].propertyDetails.stateRegionId,
                                );
                              break;
                            default:
                              break;
                          }
                        }}
                      >
                        {regionsMapping[castRegionId]}
                      </a>
                    </TextButton>
                  </Table.Cell>
                  <Table.Cell>{percentage.toFixed(1)}%</Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table.Root>
      </Table.Responsive>
    </Box>
  );
};
